import React from 'react';

interface LoadingSpinnerProps {
    size?: 'small' | 'medium' | 'large';
    message?: string;
    className?: string;
    variant?: 'default' | 'overlay' | 'card';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
    size = 'medium',
    message = 'Loading...',
    className = '',
    variant = 'default'
}) => {
    // Size mappings
    const sizeClasses = {
        small: {
            container: 'w-8 h-8',
            border: 'border-2',
            dots: 'w-1 h-1',
            textSize: 'text-xs'
        },
        medium: {
            container: 'w-12 h-12 sm:w-16 sm:h-16',
            border: 'border-4',
            dots: 'w-1.5 h-1.5 sm:w-2 sm:h-2',
            textSize: 'text-sm sm:text-base'
        },
        large: {
            container: 'w-16 h-16 sm:w-24 sm:h-24',
            border: 'border-4 sm:border-8',
            dots: 'w-2 h-2 sm:w-3 sm:h-3',
            textSize: 'text-base sm:text-lg'
        }
    };

    const currentSize = sizeClasses[size];

    const SpinnerContent = () => (
        <div className={`flex flex-col items-center justify-center ${className}`}>
            <div className={`relative mb-4 ${size === 'small' ? 'mb-2' : 'sm:mb-6'}`}>
                <div className={`${currentSize.container} ${currentSize.border} border-primary-500 border-t-transparent rounded-full animate-spin mx-auto`}></div>
                <div className={`absolute inset-0 ${currentSize.container} ${currentSize.border} border-secondary-500/30 rounded-full mx-auto animate-pulse`}></div>
            </div>

            {message && (
                <p className={`text-primary-300 font-mono tracking-wider uppercase ${currentSize.textSize} text-center`}>
                    {message}
                </p>
            )}

            <div className={`flex justify-center space-x-1 ${size === 'small' ? 'mt-1' : 'mt-3 sm:mt-4'}`}>
                <div className={`${currentSize.dots} bg-primary-500 rounded-full animate-bounce`}></div>
                <div className={`${currentSize.dots} bg-primary-500 rounded-full animate-bounce`} style={{ animationDelay: '0.1s' }}></div>
                <div className={`${currentSize.dots} bg-primary-500 rounded-full animate-bounce`} style={{ animationDelay: '0.2s' }}></div>
            </div>
        </div>
    );

    if (variant === 'overlay') {
        return (
            <div className="absolute inset-0 bg-dark-950/80 backdrop-blur-sm flex items-center justify-center z-50">
                <SpinnerContent />
            </div>
        );
    }

    if (variant === 'card') {
        return (
            <div className={`inline-block bg-gradient-to-br from-dark-800 to-dark-900 border border-primary-500/50 rounded-lg p-4 shadow-neon relative overflow-hidden ${className}`}>
                {/* Corner accents */}
                <div className="absolute top-1 left-1 w-2 h-2 border-l-2 border-t-2 border-primary-400 opacity-60"></div>
                <div className="absolute top-1 right-1 w-2 h-2 border-r-2 border-t-2 border-primary-400 opacity-60"></div>
                <div className="absolute bottom-1 left-1 w-2 h-2 border-l-2 border-b-2 border-primary-400 opacity-60"></div>
                <div className="absolute bottom-1 right-1 w-2 h-2 border-r-2 border-b-2 border-primary-400 opacity-60"></div>

                <div className="text-center relative z-10">
                    <SpinnerContent />
                </div>
            </div>
        );
    }

    return <SpinnerContent />;
};

export default LoadingSpinner;
